import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Global in-app file viewer (PDFs + images).
 *
 * WHY: downloads, blob tabs AND Chrome's PDF plugin are all blocked inside
 * sandboxed/iframed contexts (embedded previews, kiosk browsers). So PDFs are
 * displayed as server-rendered page images — bulletproof everywhere — with a
 * Download button for normal browsers.
 *
 * Any code can show a file via:
 *   window.dispatchEvent(new CustomEvent('fmla:view-file', { detail }))
 * detail: { filename, type, url? (blob), pages? [pngB64,...], downloadPath? }
 * `openAuthedFile()` in lib/api.js does exactly that.
 */
export function FileViewerHost() {
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onView = (e) => setFile(e.detail);
    window.addEventListener('fmla:view-file', onView);
    return () => window.removeEventListener('fmla:view-file', onView);
  }, []);

  const close = () => {
    if (file?.url?.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(file.url), 1000);
    setFile(null);
  };

  const triggerAnchor = (href) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = file.filename || 'document';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const download = async () => {
    setDownloading(true);
    try {
      let href = file.url;
      let revoke = false;
      if (!href && file.downloadPath) {
        const res = await api.get(file.downloadPath, { responseType: 'blob' });
        href = URL.createObjectURL(res.data);
        revoke = true;
      }
      if (!href) throw new Error('no source');
      triggerAnchor(href);
      if (revoke) setTimeout(() => URL.revokeObjectURL(href), 60_000);
      toast.success('Download started — check your downloads folder', {
        description: 'If nothing appears, this browser blocks downloads (common inside embedded previews). Open the app in its own tab and try again.',
      });
    } catch {
      toast.error('Your browser blocked the download');
    } finally {
      setDownloading(false);
    }
  };

  const hasPages = Array.isArray(file?.pages) && file.pages.length > 0;

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl" data-testid="file-viewer-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle className="text-base flex items-center gap-2 truncate">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate">{file?.filename || 'Document'}</span>
              {hasPages && <span className="text-xs font-normal text-muted-foreground shrink-0">{file.pages.length} page{file.pages.length > 1 ? 's' : ''}</span>}
            </DialogTitle>
            <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="file-viewer-download">
              {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Download
            </Button>
          </div>
        </DialogHeader>
        {file && (
          hasPages ? (
            <div className="max-h-[72vh] overflow-auto rounded-lg border bg-muted/40 p-3 space-y-3" data-testid="file-viewer-pages">
              {file.pages.map((b64, i) => (
                <img
                  key={i}
                  src={`data:image/png;base64,${b64}`}
                  alt={`${file.filename || 'Document'} — page ${i + 1}`}
                  className="w-full h-auto rounded shadow-sm bg-white"
                  data-testid={`file-viewer-pdf-page-${i}`}
                />
              ))}
            </div>
          ) : (
            <div className="max-h-[72vh] overflow-auto rounded-lg border bg-muted/30 grid place-items-center">
              <img src={file.url} alt={file.filename || 'Document'} className="max-w-full h-auto" data-testid="file-viewer-image" />
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
